'use client'

// frontend/src/components/brand/PointerGlow.tsx
// Ported unchanged from the PA (H2R) demo shell, branch L1-L2-Yash (plan 03 CHG-17).
// Mounted only in the signed-in app shell (app/layout.tsx), never on /apply or the
// sign-in pages.
//
// A soft light that follows the presenter's pointer.
//
// "From a demo perspective it might be useful, because when we are navigating
// people through some screens it will be helpful for them to see where we are
// going, where the mouse is moving." — demo prep, 20 Sep.
//
// So its job is to tell a room where the pointer is, not to be decorative. That
// is why the alpha is nearer 0.12 than the 0.07 it was ported from: the source
// sits on a near-black canvas and this product is white, where 7% is nothing.
// It is cornflower, the brand accent, because "whatever is done to the white
// screen has to stay on the existing theme".
//
// Four changes on the way over from the original:
//
//  1. Mounted alone. The source ships it through a provider that ALSO replaces
//     the system cursor with a drawn arrow, and that was explicitly ruled out —
//     "just the hover effect, not the cursor effect".
//  2. A `prefers-reduced-motion` bail-out. Every other animation in this product
//     respects it and this is not the exception.
//  3. CSS custom properties written from a rAF instead of a fresh gradient
//     string per mousemove, so a pointer sweep costs one paint per frame rather
//     than one per event, and React never re-renders.
//  4. Behind the content rather than over it. At z-index 50 the overlay tinted
//     everything under it, including dialogs.
//
// Touch devices attach no listeners at all: the effect is gated on a fine
// pointer, which is also why there is nothing to clean up on a phone.

import { useEffect, useRef } from 'react'

/**
 * THE THREE NUMBERS TO DIAL. Everything else in this file is plumbing.
 *
 * Cornflower `#8AA2DF`, the existing brand accent — the colour is decided and
 * is not a knob. `CORE` is the alpha directly under the pointer, `MID` is the
 * alpha it has fallen to two-fifths of the way out, and `RADIUS` is how far it
 * reaches. Raise CORE to make it louder; raise RADIUS to make it broader.
 *
 * It sits ABOVE the page's cards rather than behind them. Behind them it lit
 * the gutters and nothing else, because almost every pixel of this product is
 * an opaque white card — which is most of why it read as invisible. It still
 * sits below the header, the sidebar and every dialog, popover and toast, so
 * nothing you can click is ever tinted.
 *
 * Dialled DOWN on 21 Sep, one day after being dialled up. Raising the layer
 * fixed "I can't see it"; the numbers that came with it overshot in the other
 * direction, and on a projector the pool followed the pointer across half the
 * screen: "Can you reduce the effect glow of that? Because it feels like it
 * glow is much bigger." The reach was the complaint, not the presence, so the
 * radius takes most of the cut.
 *
 * Was, 20 Sep: CORE 0.30, MID 0.15, RADIUS 780.
 */
const GLOW_CORE = 'rgba(138, 162, 223, 0.22)'
const GLOW_MID = 'rgba(138, 162, 223, 0.10)'
const RADIUS = 460

/**
 * Above the content, below every overlay.
 *
 * The app's scale is dropdown 50, header 100, sidebar 200, modal 300–400,
 * popover 500, tooltip 600, toast 700. Forty is above the page and its cards —
 * which carry no z-index, or at most `z-10` — and under all of those.
 */
const GLOW_LAYER = 40

export function PointerGlow() {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (typeof window === 'undefined') return
    if (!window.matchMedia('(any-hover: hover) and (any-pointer: fine)').matches) return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

    const node = ref.current
    if (!node) return

    // Position lives in a ref and is written once per frame. Storing it in
    // state would re-render the whole tree on every mouse move.
    let x = 0
    let y = 0
    let queued = false

    const paint = () => {
      queued = false
      node.style.setProperty('--glow-x', `${x}px`)
      node.style.setProperty('--glow-y', `${y}px`)
      node.style.opacity = '1'
    }

    const onMove = (event: MouseEvent) => {
      x = event.clientX
      y = event.clientY
      if (queued) return
      queued = true
      requestAnimationFrame(paint)
    }

    const onLeave = () => {
      node.style.opacity = '0'
    }

    document.addEventListener('mousemove', onMove, { passive: true })
    document.addEventListener('mouseleave', onLeave)
    return () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseleave', onLeave)
    }
  }, [])

  return (
    <div
      ref={ref}
      aria-hidden
      className='pointer-events-none fixed inset-0 transition-opacity duration-300'
      style={{
        zIndex: GLOW_LAYER,
        opacity: 0,
        // Three stops, not two. A single stop to transparent falls away so
        // evenly that there is no bright point to follow; a held core with a
        // longer tail reads from the back of a room as a light being carried.
        background: `radial-gradient(${RADIUS}px circle at var(--glow-x, 50%) var(--glow-y, 50%), ${GLOW_CORE} 0%, ${GLOW_MID} 38%, transparent 68%)`,
      }}
    />
  )
}
